<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import naviappInboxes from 'dashboard/api/naviappInboxes';
import {
  NAVIAPP_DOCS_URL as DOCS_URL,
  isValidTenantId,
  isValidApiKey,
  naviappErrorMessage,
} from '../helpers/naviapp';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

// Caixa NAVI App: ID da empresa + chave. O backend confere a chave, cadastra o
// webhook e liga o modo integração (a Isis da empresa para de responder).

const { t } = useI18n();
const router = useRouter();
const store = useStore();

const name = ref('NAVI App');
const tenantId = ref('');
const apiKey = ref('');
const creating = ref(false);
const errorMessage = ref('');

const tenantIdValue = computed(() => tenantId.value.trim());
const apiKeyValue = computed(() => apiKey.value.trim());
const tenantIdError = computed(() =>
  tenantIdValue.value && !isValidTenantId(tenantIdValue.value)
    ? t('INBOX_MGMT.ADD.NAVIAPP.TENANT_ID_INVALID')
    : ''
);
const apiKeyError = computed(() =>
  apiKeyValue.value && !isValidApiKey(apiKeyValue.value)
    ? t('INBOX_MGMT.ADD.NAVIAPP.API_KEY_INVALID')
    : ''
);
const canSubmit = computed(
  () =>
    !!name.value.trim() &&
    isValidTenantId(tenantIdValue.value) &&
    isValidApiKey(apiKeyValue.value)
);

const createInbox = async () => {
  if (!canSubmit.value || creating.value) return;
  creating.value = true;
  errorMessage.value = '';
  try {
    const { data } = await naviappInboxes.create({
      name: name.value.trim(),
      tenant_id: tenantIdValue.value,
      api_key: apiKeyValue.value,
    });
    await store.dispatch('inboxes/get');
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: data.inbox_id },
    });
  } catch (error) {
    errorMessage.value = naviappErrorMessage(error, t);
  } finally {
    creating.value = false;
  }
};
</script>

<template>
  <div class="w-full h-full col-span-6 p-6 overflow-auto">
    <h2 class="text-lg font-medium text-n-slate-12">
      {{ t('INBOX_MGMT.ADD.NAVIAPP.TITLE') }}
    </h2>
    <p class="mt-1 text-sm text-n-slate-11">
      {{ t('INBOX_MGMT.ADD.NAVIAPP.DESC') }}
    </p>
    <a
      :href="DOCS_URL"
      target="_blank"
      rel="noopener noreferrer"
      class="inline-block mt-2 text-sm text-n-blue-11 hover:underline"
    >
      {{ t('INBOX_MGMT.ADD.NAVIAPP.HELP_LINK') }}
    </a>
    <p
      data-test="naviapp-warning"
      class="max-w-md p-3 mt-4 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
    >
      {{ t('INBOX_MGMT.ADD.NAVIAPP.WARNING') }}
    </p>
    <form
      class="flex flex-col gap-4 mt-6 max-w-md"
      @submit.prevent="createInbox"
    >
      <Input
        v-model="name"
        :label="t('INBOX_MGMT.ADD.NAVIAPP.NAME_LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.NAVIAPP.NAME_PLACEHOLDER')"
      />
      <Input
        v-model="tenantId"
        :label="t('INBOX_MGMT.ADD.NAVIAPP.TENANT_ID_LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.NAVIAPP.TENANT_ID_PLACEHOLDER')"
        :message="tenantIdError"
        :message-type="tenantIdError ? 'error' : 'info'"
      />
      <Input
        v-model="apiKey"
        type="password"
        :label="t('INBOX_MGMT.ADD.NAVIAPP.API_KEY_LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.NAVIAPP.API_KEY_PLACEHOLDER')"
        :message="apiKeyError"
        :message-type="apiKeyError ? 'error' : 'info'"
      />
      <p
        v-if="errorMessage"
        data-test="naviapp-error"
        class="text-sm text-n-ruby-11"
      >
        {{ errorMessage }}
      </p>
      <div>
        <Button
          type="submit"
          :label="t('INBOX_MGMT.ADD.NAVIAPP.CREATE')"
          :is-loading="creating"
          :disabled="!canSubmit || creating"
        />
      </div>
    </form>
  </div>
</template>
